import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Search, Loader2, X, Check } from 'lucide-react';
import { clinicApi } from '@features/clinics/services/clinicApi';
import { searchCustomers, searchCustomerLocations, getCustomerLocations } from '@features/coverage/services/customerApi';
import type { ManualMatchEntityType } from '../../types/cutover';
import { withXmId } from '@shared/utils/xmId';

export interface MatchCandidate {
  id: number;
  name: string;
  detail?: string;
}

interface ManualMatchPopoverProps {
  entityType: ManualMatchEntityType;
  /** For location search, pass the already-resolved customer ID for scoping */
  customerId?: number;
  /** Single-select callback (customer / clinic) */
  onSelect: (id: number, name: string) => void;
  /** Multi-select callback (locations) — called with array of {id, name} */
  onMultiSelect?: (selections: { id: number; name: string }[]) => void;
  onClose: () => void;
  /** Anchor element for positioning */
  anchorEl: HTMLElement | null;
  /** Pre-fill search and auto-search on open (for ambiguous matches) */
  initialQuery?: string;
  /** Pre-selected location IDs (for editing existing multi-selection) */
  preselectedIds?: Set<number>;
  /** Pre-loaded candidates from fuzzy matching — shown immediately, no API search needed */
  candidates?: MatchCandidate[];
}

export default function ManualMatchPopover({
  entityType,
  customerId,
  onSelect,
  onMultiSelect,
  onClose,
  anchorEl,
  initialQuery,
  preselectedIds,
  candidates,
}: Readonly<ManualMatchPopoverProps>) {
  const hasCandidates = candidates != null && candidates.length > 0;
  const [query, setQuery] = useState(hasCandidates ? '' : (initialQuery ?? ''));
  const [results, setResults] = useState<MatchCandidate[]>(hasCandidates ? candidates : []);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(hasCandidates);
  const popoverRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const didAutoSearch = useRef(false);

  // For location entity with a known customer, load ALL locations upfront
  // and filter client-side instead of server-searching
  const [allLocations, setAllLocations] = useState<MatchCandidate[] | null>(null);
  const isPreloadMode = entityType === 'location' && customerId != null;
  const isMultiSelect = isPreloadMode && onMultiSelect != null;

  // Multi-select checkbox state (map of id → name for checked items)
  const [checked, setChecked] = useState<Map<number, string>>(
    () => {
      if (!preselectedIds) return new Map();
      // We'll populate names once locations load; for now just track IDs
      const m = new Map<number, string>();
      preselectedIds.forEach((id) => m.set(id, ''));
      return m;
    },
  );

  // Once locations load, fill in names for pre-selected IDs
  useEffect(() => {
    if (!allLocations || !preselectedIds || preselectedIds.size === 0) return;
    setChecked((prev) => {
      let changed = false;
      const next = new Map(prev);
      for (const loc of allLocations) {
        if (preselectedIds.has(loc.id) && !next.get(loc.id)) {
          next.set(loc.id, loc.name);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [allLocations, preselectedIds]);

  const toggleChecked = useCallback((id: number, name: string) => {
    setChecked((prev) => {
      const next = new Map(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.set(id, name);
      }
      return next;
    });
  }, []);

  // Focus input on open
  useEffect(() => {
    const timer = setTimeout(() => inputRef.current?.focus(), 50);
    return () => clearTimeout(timer);
  }, []);

  // Click-outside handler
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node) &&
        anchorEl &&
        !anchorEl.contains(e.target as Node)
      ) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose, anchorEl]);

  // Escape key handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Pre-load ALL locations for the customer (when in preload mode)
  useEffect(() => {
    if (!isPreloadMode || !customerId) return;
    let cancelled = false;
    setIsLoading(true);
    getCustomerLocations(customerId)
      .then((locations) => {
        if (cancelled) return;
        const candidates = locations.map((l) => ({
          id: l.id,
          // XM_ID beside the name (SLV2-2823) — same as the search branch below;
          // the preload branch was missed in the original pass
          name: withXmId(l.name, l.xmId),
          detail: [l.city, l.state].filter(Boolean).join(', ') || undefined,
        }));
        setAllLocations(candidates);
        setHasSearched(true);
      })
      .catch(() => {
        if (!cancelled) setAllLocations([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => { cancelled = true; };
  }, [isPreloadMode, customerId]);

  // Client-side filtered results for preload mode
  const filteredLocations = useMemo(() => {
    if (!allLocations) return [];
    if (!query.trim()) return allLocations;
    const lower = query.toLowerCase();
    return allLocations.filter(
      (l) =>
        l.name.toLowerCase().includes(lower) ||
        (l.detail && l.detail.toLowerCase().includes(lower)),
    );
  }, [allLocations, query]);

  const doSearch = useCallback(
    async (searchQuery: string) => {
      if (searchQuery.length < 2) {
        setResults([]);
        setHasSearched(false);
        return;
      }
      setIsLoading(true);
      setHasSearched(true);
      try {
        let candidates: MatchCandidate[] = [];
        switch (entityType) {
          case 'customer': {
            const customers = await searchCustomers(searchQuery, 15);
            candidates = customers.map((c) => ({
              id: c.id,
              // XM_ID beside the name (SLV2-2823) — identically-named companies
              // (six Examinetics…) are only distinguishable by their Navision id
              name: withXmId(c.name, c.xmId),
              detail: c.accountNumber ?? undefined,
            }));
            break;
          }
          case 'clinic': {
            const page = await clinicApi.getClinics(1, 15, searchQuery);
            candidates = page.items.map((c) => ({
              id: c.id,
              name: c.name,
              detail: [c.code, c.city, c.state].filter(Boolean).join(', ') || undefined,
            }));
            break;
          }
          case 'location': {
            const locations = await searchCustomerLocations(searchQuery, customerId, 15);
            candidates = locations.map((l) => ({
              id: l.id,
              name: withXmId(l.name, l.xmId),
              detail:
                [l.city, l.state].filter(Boolean).join(', ') ||
                (l.customerName ? withXmId(l.customerName, l.customerXmId) : undefined),
            }));
            break;
          }
        }
        setResults(candidates);
      } catch {
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    },
    [entityType, customerId],
  );

  // Auto-search on open when initialQuery is provided (no debounce) — only for non-preload mode
  useEffect(() => {
    if (isPreloadMode || hasCandidates) return;
    if (initialQuery && initialQuery.length >= 2 && !didAutoSearch.current) {
      didAutoSearch.current = true;
      doSearch(initialQuery);
    }
  }, [initialQuery, doSearch, isPreloadMode, hasCandidates]);

  // Debounced search for user-typed queries — only for non-preload mode
  useEffect(() => {
    if (isPreloadMode) return;
    // When candidates are pre-loaded and user hasn't typed anything, keep showing candidates
    if (hasCandidates && !query) return;
    // Skip if this is the initial mount with initialQuery (already handled above)
    if (initialQuery && !didAutoSearch.current) return;
    if (initialQuery && query === initialQuery && didAutoSearch.current) return;

    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (query.length < 2) {
      // If we have candidates and user cleared the search, revert to showing candidates
      if (hasCandidates) {
        setResults(candidates!);
        setHasSearched(true);
      } else {
        setResults([]);
        setHasSearched(false);
      }
      return;
    }
    searchTimer.current = setTimeout(() => doSearch(query), 300);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [query, doSearch, initialQuery, isPreloadMode, hasCandidates, candidates]);

  // Position the popover below the anchor, flipping above when the space
  // below is too small, and always capping height to what fits on screen
  const popoverStyle: React.CSSProperties = {};
  if (anchorEl) {
    const rect = anchorEl.getBoundingClientRect();
    const margin = 8;
    const gap = 4;
    const spaceBelow = window.innerHeight - rect.bottom - gap - margin;
    const spaceAbove = rect.top - gap - margin;
    popoverStyle.position = 'fixed';
    popoverStyle.left = Math.max(margin, Math.min(rect.left, window.innerWidth - 320 - margin));
    popoverStyle.zIndex = 9999;
    if (spaceBelow < 280 && spaceAbove > spaceBelow) {
      popoverStyle.bottom = window.innerHeight - rect.top + gap;
      popoverStyle.maxHeight = spaceAbove;
    } else {
      popoverStyle.top = rect.bottom + gap;
      popoverStyle.maxHeight = spaceBelow;
    }
  }

  const entityLabel =
    entityType === 'customer' ? 'customer' : entityType === 'clinic' ? 'clinic' : 'location';

  const headerLabel = (() => {
    if (isMultiSelect) return `Pick locations (${allLocations?.length ?? '...'})`;
    if (isPreloadMode) return `Pick ${entityLabel} (${allLocations?.length ?? '...'})`;
    if (hasCandidates) return `Pick ${entityLabel} (${candidates.length} matches)`;
    if (initialQuery) return `Pick ${entityLabel}`;
    return `Search ${entityLabel}`;
  })();

  // Decide which items to display
  const displayItems = isPreloadMode ? filteredLocations : results;
  const showNoResults = isPreloadMode
    ? allLocations != null && filteredLocations.length === 0
    : hasSearched && results.length === 0;

  const handleConfirmMulti = () => {
    if (!onMultiSelect) return;
    const selections = Array.from(checked.entries()).map(([id, name]) => ({ id, name }));
    onMultiSelect(selections);
  };

  return (
    <div ref={popoverRef} className="cmit-match-popover" style={popoverStyle}>
      <div className="cmit-match-popover-header">
        <span className="cmit-match-popover-title">{headerLabel}</span>
        <button
          type="button"
          className="cmit-match-popover-close"
          onClick={onClose}
          aria-label="Close"
        >
          <X size={14} />
        </button>
      </div>
      <div className="cmit-match-popover-search">
        <Search size={14} className="cmit-match-popover-search-icon" />
        <input
          ref={inputRef}
          type="text"
          className="cmit-match-popover-input"
          placeholder={isPreloadMode ? 'Filter locations...' : `Type to search ${entityLabel}s...`}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {query && (
          <button
            type="button"
            className="cmit-match-popover-input-clear"
            onClick={() => {
              setQuery('');
              if (!isPreloadMode) {
                setResults([]);
                setHasSearched(false);
              }
              inputRef.current?.focus();
            }}
            aria-label="Clear search"
          >
            <X size={12} />
          </button>
        )}
      </div>
      <div className="cmit-match-popover-results">
        {isLoading ? (
          <div className="cmit-match-popover-empty">
            <Loader2 size={14} className="cmit-spin" />
            <span>{isPreloadMode ? 'Loading locations...' : 'Searching...'}</span>
          </div>
        ) : displayItems.length > 0 ? (
          displayItems.map((r) => (
            isMultiSelect ? (
              <label
                key={r.id}
                className={`cmit-match-popover-item cmit-match-popover-item-checkbox ${checked.has(r.id) ? 'checked' : ''}`}
              >
                <span className={`cmit-match-checkbox ${checked.has(r.id) ? 'checked' : ''}`}>
                  {checked.has(r.id) && <Check size={10} />}
                </span>
                <button
                  type="button"
                  className="cmit-match-popover-item-btn"
                  onClick={() => toggleChecked(r.id, r.name)}
                >
                  <span className="cmit-match-popover-item-name">{r.name}</span>
                  {r.detail && (
                    <span className="cmit-match-popover-item-detail">{r.detail}</span>
                  )}
                </button>
              </label>
            ) : (
              <button
                key={r.id}
                type="button"
                className="cmit-match-popover-item"
                onClick={() => onSelect(r.id, r.name)}
              >
                <span className="cmit-match-popover-item-name">{r.name}</span>
                {r.detail && (
                  <span className="cmit-match-popover-item-detail">{r.detail}</span>
                )}
              </button>
            )
          ))
        ) : showNoResults ? (
          <div className="cmit-match-popover-empty">
            {isPreloadMode && query ? 'No locations match filter' : 'No results found'}
          </div>
        ) : !isPreloadMode && initialQuery ? (
          <div className="cmit-match-popover-empty">
            <Loader2 size={14} className="cmit-spin" />
            <span>Loading matches...</span>
          </div>
        ) : !isPreloadMode ? (
          <div className="cmit-match-popover-empty">Type at least 2 characters to search</div>
        ) : null}
      </div>
      {isMultiSelect && (
        <div className="cmit-match-popover-footer">
          <span className="cmit-match-popover-footer-count">
            {checked.size} selected
          </span>
          <button
            type="button"
            className="cmit-match-popover-footer-done"
            onClick={handleConfirmMulti}
            disabled={checked.size === 0}
          >
            Done
          </button>
        </div>
      )}
    </div>
  );
}
